import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Group, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { Alert, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconActivity } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { GroupBy, TransactionItem, TransactionSort } from "@models/adminMonitoring";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { useMonitoringFilters } from "@hooks/monitoring/useMonitoringFilters";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { SourceAlerts, TransactionsTable, WindowToolbar } from "@features/Insights";

const PAGE_SIZE = 25;
const SORTS: TransactionSort[] = ["last_used", "first_used", "cost", "events"];

/**
 * Every metered transaction of every module and service, as one table. The
 * window and filters narrow it (filter values come from the data), a row opens
 * the transaction with its events. Totals and charts live on the dashboard.
 */
export const MonitoringPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canViewAllOrganisations } = useAdminPermissions();
  const monitoring = useMonitoringFilters();
  const { values } = monitoring;
  // Only the window, organisation and module filter this page; other filter keys a
  // link may carry are ignored rather than applied invisibly.
  const filters = {
    from: monitoring.filters.from,
    to: monitoring.filters.to,
    organisationId: monitoring.filters.organisationId,
    module: monitoring.filters.module,
  };
  const key = JSON.stringify(filters);
  const [params, setParams] = useSearchParams();

  const requested = params.get("group");
  const groupBy: GroupBy = requested === "reference_type" || requested === "root" ? requested : "reference";
  const setGroupBy = (value: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value === "reference_type" || value === "root") next.set("group", value);
      else next.delete("group");
      return next;
    }, { replace: true });

  const [sort, setSort] = useState<TransactionSort>("last_used");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [key, groupBy, sort]);

  const facets = useApiResource(`facets:${key}`, (s) => adminMonitoringApi().facets(filters, s));
  const transactions = useApiResource(`tx:${key}:${groupBy}:${sort}:${page}`, (s) =>
    adminMonitoringApi().transactions(filters, { groupBy, sort, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }, s));

  const showOrgs = Boolean(canViewAllOrganisations && !values.org);
  const open = (item: TransactionItem) =>
    navigate(`/admin/monitoring/transactions/${item.group_id}?${new URLSearchParams({ from: filters.from, to: filters.to })}`);
  const error = transactions.error ?? facets.error;

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.insights.monitoring.title")}
        description={t("admin.insights.monitoring.description")}
        icon={<IconActivity size={28} />}
        actions={
          <WindowToolbar
            preset={monitoring.preset}
            custom={monitoring.custom}
            onPresetChange={monitoring.setPreset}
            onRefresh={() => {
              monitoring.refresh();
              facets.reload();
              transactions.reload();
            }}
            loading={transactions.loading}
            organisations={canViewAllOrganisations ? facets.data?.organisations ?? [] : undefined}
            organisationId={values.org}
            onOrganisationChange={(value) => monitoring.setFilter("org", value)}
            modules={facets.data?.modules ?? []}
            moduleValue={values.module}
            onModuleChange={(value) => monitoring.setFilter("module", value)}
          />
        }
      />

      {error && (
        <Alert color={transactions.forbidden ? "gray" : "red"} title={t("admin.insights.loadFailed")}>{error}</Alert>
      )}
      <SourceAlerts sources={transactions.data?.sources} />

      <SectionCard
        title={
          <Group gap="xs">
            <Text fw={600}>{t("admin.insights.monitoring.tabs.transactions")}</Text>
            {transactions.data && (
              <Text size="sm" c="dimmed">
                {t("admin.insights.monitoring.transactionCount", { count: transactions.data.total })}
              </Text>
            )}
          </Group>
        }
        actions={
          <Group gap="sm">
            <SegmentedControl
              size="xs"
              value={groupBy}
              onChange={setGroupBy}
              data={[
                { value: "reference", label: t("admin.insights.monitoring.groupBy.reference") },
                { value: "reference_type", label: t("admin.insights.monitoring.groupBy.reference_type") },
                { value: "root", label: t("admin.insights.monitoring.groupBy.root") },
              ]}
            />
            <Select
              size="xs"
              w={170}
              value={sort}
              onChange={(value) => value && setSort(value as TransactionSort)}
              allowDeselect={false}
              aria-label={t("admin.insights.monitoring.sort.label")}
              data={SORTS.map((value) => ({ value, label: t(`admin.insights.monitoring.sort.${value}`) }))}
            />
          </Group>
        }
      >
        <Stack gap="md">
          {groupBy !== "reference" && (
            <Text size="xs" c="dimmed">
              {t(groupBy === "root" ? "admin.insights.monitoring.groupBy.rootHint" : "admin.insights.monitoring.groupBy.parentHint")}
            </Text>
          )}
          <TransactionsTable
            items={transactions.data?.items ?? []}
            loading={transactions.loading}
            showOrganisation={showOrgs}
            onOpen={open}
            pagination={transactions.data && transactions.data.total > PAGE_SIZE
              ? { page, total: transactions.data.total, pageSize: PAGE_SIZE, onChange: setPage }
              : undefined}
          />
        </Stack>
      </SectionCard>
    </Stack>
  );
};
